/**
 * AuthenticatedImage Component
 * 
 * Fetches images through authenticated API endpoints and displays them.
 * Needed because img tags can't send Authorization headers.
 */

import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faImage, faCircleNotch } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { fetchScreenshotAsBlob } from "@/services/api/opsChecklists";

interface AuthenticatedImageProps {
  src: string;
  alt: string;
  className?: string;
  onClick?: () => void;
}

export function AuthenticatedImage({ src, alt, className, onClick }: AuthenticatedImageProps) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let mounted = true;
    
    const loadImage = async () => {
      setIsLoading(true);
      setError(false);
      
      const url = await fetchScreenshotAsBlob(src);
      
      if (mounted) {
        if (url) {
          setBlobUrl(url);
        } else {
          setError(true);
        }
        setIsLoading(false);
      }
    };

    loadImage();

    return () => {
      mounted = false;
      // Clean up blob URL when component unmounts
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [src]);

  /** Use inline-level wrappers only — markdown renders images inside `<p>`; `<div>` breaks HTML + hydration. */
  if (isLoading) {
    return (
      <span
        className={`inline-flex items-center justify-center bg-slate-100 dark:bg-slate-800 rounded ${className ?? ""}`}
        style={{ minHeight: "60px", minWidth: "80px", verticalAlign: "middle" }}
      >
        <FontAwesomeIcon icon={faCircleNotch} className="text-slate-400 animate-spin" />
      </span>
    );
  }

  if (error || !blobUrl) {
    return (
      <span
        className={`inline-flex items-center justify-center bg-slate-100 dark:bg-slate-800 rounded ${className ?? ""}`}
        style={{ minHeight: "60px", minWidth: "80px", verticalAlign: "middle" }}
      >
        <FontAwesomeIcon icon={faImage} className="text-slate-400" />
      </span>
    );
  }

  return (
    <img 
      src={blobUrl} 
      alt={alt} 
      className={className}
      onClick={onClick}
    />
  );
}
